import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

const SubscriptionSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const userEmail = searchParams.get('email');

  useEffect(() => {
    if (sessionId) {
      // In a real implementation, you might want to verify the session with your backend
      // For now, we'll just show the success message
      setLoading(false);
    } else {
      setError('No session ID found');
      setLoading(false);
    }
  }, [sessionId]);

  const handleGoToDashboard = () => {
    navigate('/portal/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md">
            <h1 className="text-2xl font-bold text-red-800 mb-4">Error</h1>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/subscription')}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Back to Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Welcome to Receevit®! 🎉
          </h1>
          
          <p className="text-lg text-gray-600 mb-6">
            Your subscription has been successfully created and your 30-day free trial is now active. 
            You can start sending and receiving B2B invoices immediately.
          </p>
          
          {userEmail && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">
                <strong>Account created for:</strong> {userEmail}
              </p>
            </div>
          )}

          {/* Trial Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3">
              🚀 Your Free Trial is Active
            </h3>
            <p className="text-blue-800 text-sm mb-3">
              You now have access to your selected plan features. Your trial ends in 30 days, 
              and you won't be charged until then.
            </p>
            
            <div className="text-left">
              <h4 className="font-medium text-blue-900 mb-2">Your Plan Features:</h4>
              <ul className="space-y-1 text-xs text-blue-800">
                <li>• Send and receive B2B invoices</li>
                <li>• Automatic invoice approval (7-day rule)</li>
                <li>• Automatic payment collection</li>
                <li>• Track payment statuses</li>
                <li>• Basic analytics dashboard</li>
                <li>• 1 user account</li>
              </ul>
            </div>
          </div>

          {/* Next Steps */}
          <div className="text-left mb-8">
            <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Complete your profile setup
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Send your first B2B invoice
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Track payment statuses and approvals
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Monitor your dashboard analytics
              </li>
            </ul>
          </div>

          {/* Action Button */}
          <div className="space-y-3">
            <button
              onClick={handleGoToDashboard}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
            >
              Go to Dashboard
            </button>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <p>
              <strong>Note:</strong> You'll need to log in with your email and password to access your dashboard.
            </p>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-xs text-gray-500">
            <p>
              Need help? Contact our support team at{' '}
              <a href="mailto:support@receevit.com" className="text-blue-600 hover:underline">
                support@receevit.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
